import { useCallback, useEffect, useRef } from "react";

/**
 * Throttle a callback to fire at most once per animation frame.
 */
export function useRafThrottle<T extends (...args: unknown[]) => unknown>(
  callback: T
): (...args: Parameters<T>) => void {
  const rafRef = useRef<number | null>(null);
  const callbackRef = useRef<T>(callback);

  // Callers pass a fresh closure every render, so the latest-ref sync must run
  // on every commit; a [callback] dep would just be a reference that never
  // matches (and makes callers churn this effect on each render).
  useEffect(() => {
    callbackRef.current = callback;
  });

  const throttledCallback = useCallback((...args: Parameters<T>) => {
    // Skip if we already have a frame queued
    if (rafRef.current) {
      return;
    }

    rafRef.current = requestAnimationFrame(() => {
      callbackRef.current(...args);
      rafRef.current = null;
    });
  }, []);

  // Clean up any pending animation frame on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return throttledCallback;
}
